import { z } from 'zod';
import { ulidSchema } from './id';

/**
 * Card covers — the contract layer for block **B25**, `FR-CARD-025`/
 * `FR-BOARD-031`.
 *
 * ⚠ **"`FR-CARD-021`" stood on this line and at every other cover citation in
 * this file through stage 1, and it was wrong — `FR-CARD-021` is "Convert a
 * checklist item into a card" (`docs/requirements/02-card-detail.md`), an
 * unrelated B26 requirement. The cover requirement is `FR-CARD-025`, "Set a
 * card cover", confirmed against `00-overview-and-mvp-scope.md`'s own index
 * and against `db/postgres/migrations/0002_boards_lists_cards.sql`'s
 * `cover_photo_attribution` comment, which has cited `FR-CARD-025` correctly
 * since the column was written. Corrected in place here, in
 * `./board-contents.ts` and in `./card-detail.ts` — the three files `BE-25.2`
 * touches. The misattribution recurs in `./card.ts`, `./comment.test.ts`,
 * `./label.test.ts`, `./cross-board-move.test.ts` and two files under
 * `apps/server/`, none of which this block edits; a later pass should sweep
 * them together rather than one at a time, since several of those files pin
 * assertions to marker phrases and a scattershot edit risks moving one.**
 *
 * ⚠ **"THIS FILE IS THE VIEW HALF ONLY" stood here through stage 1 and is now
 * false: `BE-25.2` is this stage, and the input half arrives below.** Stage 1
 * (`BE-25.1`, the card-front badge projection) needed a cover *shape* because
 * `cover` is one of the four badge fields `FR-BOARD-030` lists and a column on
 * the row `GET /boards/:id/contents`'s card statement already selects — but
 * it wrote no cover, so it validated none on the way in. `PUT`/
 * `DELETE /cards/:id/cover` (`apps/server/src/routes/card-cover.ts`),
 * `cardCoverPutSchema`, `COVER_COLOURS` (the fixed ten-member palette
 * `FR-BOARD-031` names) and `coverColourSchema` (the input-side enum) are
 * declared below, on `./card.ts`'s own rule: a schema arrives with the route
 * that reads or writes it, and the route that writes a cover has now landed.
 * They join `cardCoverSchema`, `coverSizeSchema` and `coverTextTreatmentSchema`
 * in this one file rather than a second — every other noun in this package
 * (`./card-dates`, `./label`, `./card-detail`) keeps its input and its view
 * together, and a cover is no exception now that it has both.
 *
 * ---------------------------------------------------------------------------
 * ONE SHAPE, THREE VARIANTS, DISCRIMINATED ON `source` — `ck_cards_cover_
 * source`'s OWN THREE MEMBERS
 * ---------------------------------------------------------------------------
 * `cover_source` is `colour | photo_library | attachment` (measured against
 * the live `ck_cards_cover_source`), and `cardCoverSchema` below is a
 * `z.discriminatedUnion('source', […])` over exactly those three literals,
 * each carrying only the payload its source needs — never a `z.object` with
 * every field optional, which would make `{ source: 'colour', attachmentId:
 * … }` a value that PARSES.
 *
 * ⚠ **`cover_type` is NOT on this shape, and it never will be on the view
 * either.** `cover_type` (`none | colour | image`) is *derived* from
 * `source` — `colour` → `'colour'`, `photo_library`/`attachment` →
 * `'image'` — by the pure `deriveCoverType` function `BE-25.2` declares
 * server-side, and putting it on the wire (in either direction) would be a
 * second encoding of a fact `source` already carries, on B21's refusal of a
 * `direction` field for `list.moved_between_boards`. A card with **no**
 * cover is not a fourth variant of this union: it is `cover: null` on the
 * schema that nests this one, exactly as `cardListRefSchema`'s absence is
 * never modelled as a variant of `cardListRefSchema` itself.
 *
 * ⚠ **`colour` is a bounded string here, NOT `z.enum(COVER_COLOURS)`.**
 * `cards.cover_colour` carries **no CHECK constraint** (measured — unlike
 * `ck_labels_colour`, which is the only thing that makes `labelColourSchema`
 * honest as an enum on both directions). `publicProfileSchema.avatarUrl`'s
 * rule is the one to apply: *reject on the way in, render what is there on
 * the way out.* An enum here would make one off-palette row 500 the entire
 * `GET /boards/:id/contents` — every card on the board, not the one card
 * that carries the stray value — which is a blast radius no rendering
 * convenience is worth. `BE-25.2`'s `COVER_COLOURS` is the input-side
 * enforcement; nothing enforces the palette on a read, by design.
 *
 * ⚠ **No URL field of any kind, on any variant — not now and not at
 * `BE-25.2`.** `photoRef` is verbatim and opaque (the column is `_ref`, not
 * `_url`; nothing resolves it, nothing is fetched), and `attachmentId` is
 * the wire value for the attachment variant — `BE-27.1`/`BE-27.2` own
 * signing a URL for it, later, on a route that permission-checks first. A
 * `coverUrl` field "for later" would be the SC-3 violation in miniature: a
 * signed URL sitting in a cacheable board-face payload nobody asked to
 * authorise.
 */

// ---------------------------------------------------------------------------
// Enumerated fields shared by every variant. Both are CHECK-constrained
// (`ck_cards_cover_size`, `ck_cards_cover_text_treatment`), so the enum on
// the view is honest — the same class of column as `boardSchema.visibility`,
// not `cards.cover_colour`'s.
// ---------------------------------------------------------------------------

/** `cards.cover_size` — `ck_cards_cover_size` restated (`normal | full`). */
export const coverSizeSchema = z.enum(['normal', 'full']);

export type CoverSize = z.infer<typeof coverSizeSchema>;

/**
 * `cards.cover_text_treatment` — `ck_cards_cover_text_treatment` restated
 * (`light | dark`), the badge/title contrast a full cover needs.
 */
export const coverTextTreatmentSchema = z.enum(['light', 'dark']);

export type CoverTextTreatment = z.infer<typeof coverTextTreatmentSchema>;

// ---------------------------------------------------------------------------
// INPUT primitives — what `PUT /cards/:id/cover` ACCEPTS. `./card.ts`'s
// input/view ordering, restored now that this file has both.
// ---------------------------------------------------------------------------

/**
 * The fixed palette `FR-BOARD-031` names — "a colour from a fixed palette of
 * ten" — and Trello's own ten cover colours, which is the closest thing to an
 * authority the corpus offers: the requirement names a count and not a list,
 * and neither `docs/requirements/` nor `db/postgres/migrations/0002_boards_
 * lists_cards.sql`'s header names the ten. `sky` and `lime` are already house
 * vocabulary — both are `LABEL_COLOURS` members in `./label.ts` — which is
 * coincidence rather than reuse: this array is declared here, independently,
 * on `./card.ts`'s rule against sharing a constant across two columns.
 *
 * ⚠ **Ten, and pinned by a unit assertion rather than trusted by eye** —
 * `card-cover.test.ts`'s `COVER_COLOURS.length === 10` is the enforcement,
 * because there is no `ck_cards_cover_colour` for a mismatch to surface
 * against (see `coverColourSchema` and `cardCoverSchema.colour` above).
 */
export const COVER_COLOURS = [
  'green',
  'yellow',
  'orange',
  'red',
  'purple',
  'blue',
  'sky',
  'lime',
  'pink',
  'black',
] as const;

export type CoverColour = (typeof COVER_COLOURS)[number];

/**
 * A colour as `PUT /cards/:id/cover` accepts it — `z.enum(COVER_COLOURS)`,
 * and the asymmetry with `cardCoverSchema.colour` (a bounded `string`, not
 * this enum) is deliberate and stated once, at the module header, rather than
 * at each end: `cards.cover_colour` carries no CHECK, so this enum is the
 * *only* enforcement of the palette that exists anywhere — reject on the way
 * in, render what is there on the way out.
 */
export const coverColourSchema = z.enum(COVER_COLOURS);

/**
 * `PUT /cards/:id/cover` — `FR-CARD-025`, `BE-25.2`. Sets or replaces the
 * whole cover in one request, including a size-only or text-treatment-only
 * change: there is deliberately no `PATCH /cards/:id/cover/size`, on
 * `cardDescriptionPatchSchema`'s "no `PUT /cards/:id`" argument turned around
 * — this is the ONE route on the cover, and SC-4's field-scoping does not
 * apply to a value that is one fact with three mutually exclusive shapes.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE SAME THREE VARIANTS AS `cardCoverSchema`, `z.strictObject` RATHER
 * THAN `z.object`, AND `colour` NARROWED TO THE ENUM
 * ---------------------------------------------------------------------------
 * `z.strictObject` is what turns an illegal combination into a 422 naming the
 * offending key rather than a value that parses by silently dropping it —
 * `{ source: 'colour', attachmentId: '…' }` is refused for carrying a key the
 * `colour` variant does not declare, not merely for missing `colour` (which a
 * lax `z.object` would also catch, but only by coincidence: the `.strict()`
 * refusal is unconditional and does not depend on which required field is
 * absent). This is `boardVisibilityPatchSchema`'s (`./board.ts`) shape one
 * table over — a discriminated union of `z.strictObject`s used directly as a
 * route body, the shipped precedent that this body compiles into a JSON
 * Schema Fastify's `ajv` instance accepts.
 *
 * `size` and `textTreatment` are **required on every variant**, never
 * `.default()`ed. A defaulted field would turn an *omission* into a silent
 * reset — a client re-sending only `{ source: 'colour', colour: 'blue' }` to
 * change the colour of a `full`, `dark`-text cover would silently flip it
 * back to `normal`/`light`, because "sets or replaces the whole cover" above
 * is this route's whole contract and there is no partial-update spelling.
 * Required makes an incomplete body a 422 instead of a data change nobody
 * asked for.
 *
 * `photoAttribution` is required-but-nullable, matching `cardCoverSchema`'s
 * view and `cardDueDatePatchSchema.dueAt`'s house shape for "the field must
 * be addressed, and `null` is one of its legal answers" — there is no
 * `.optional()` here for the same reason there is none on any other
 * `z.strictObject` patch body in this package: an omitted key and a `null`
 * key must not be two spellings of one intention.
 */
export const cardCoverPutSchema = z.discriminatedUnion('source', [
  z.strictObject({
    source: z.literal('colour'),
    colour: coverColourSchema.describe(
      'One of the ten fixed cover colours (FR-BOARD-031). An off-palette ' +
        'value is a 422 naming this field — the enum is the only place the ' +
        'palette is enforced, since cards.cover_colour carries no CHECK.',
    ),
    size: coverSizeSchema,
    textTreatment: coverTextTreatmentSchema,
  }),
  z.strictObject({
    source: z.literal('photo_library'),
    /**
     * Verbatim and opaque, on `cardCoverSchema.photoRef`'s rule: no scheme is
     * assumed and nothing is fetched or validated beyond this length bound.
     * `.min(1)` refuses an empty string, which is not a legal reference to
     * anything a photo-library provider could return — SG-7's "the
     * integration has no owner, do not invent one" posture stops well short
     * of accepting a request that names nothing.
     */
    photoRef: z.string().min(1).max(2048),
    photoAttribution: z.string().max(512).nullable(),
    size: coverSizeSchema,
    textTreatment: coverTextTreatmentSchema,
  }),
  z.strictObject({
    source: z.literal('attachment'),
    /**
     * The attachment must already belong to THIS card —
     * `apps/server/src/routes/card-cover.ts`'s `isAttachmentOnCard`, checked
     * inside the transaction, one `422` naming this field for both "no such
     * attachment" and "an attachment on another card". A `ulidSchema` alone
     * cannot ask that question, exactly as `addCardMemberBodySchema.userId`
     * cannot ask whether the id names a board member — the shape validates
     * only that the value could name *something*.
     */
    attachmentId: ulidSchema,
    size: coverSizeSchema,
    textTreatment: coverTextTreatmentSchema,
  }),
]);

export type CardCoverPut = z.infer<typeof cardCoverPutSchema>;

// ---------------------------------------------------------------------------
// Views — what a route RETURNS. `cardCoverSchema` is nullable wherever it is
// nested; `null` means `cards.cover_type = 'none'`, the ordinary state of
// every card in the product today until a `PUT` sets one.
// ---------------------------------------------------------------------------

/**
 * A card's cover, as `boardFaceCardSchema.cover` and `cardDetailSchema.cover`
 * both carry it — `FR-CARD-025`, `FR-BOARD-031`.
 *
 * Nullable at the call site, never here: this schema describes a cover that
 * *exists*. `readCover` in `apps/server/src/cards/cover.ts` is the pure
 * function that turns the row's raw columns into a value of this shape, or
 * `null` — including for the rows that are legally representable but
 * incoherent (an `attachment`-sourced cover whose attachment row was since
 * deleted, `fk_cards_cover_attachment_id`'s `ON DELETE SET NULL`; a `colour`
 * source with a null colour; and so on), because the failure mode for a
 * cover this shape cannot express must be "the badge is absent", never "the
 * board face 500s".
 */
export const cardCoverSchema = z.discriminatedUnion('source', [
  z.object({
    source: z.literal('colour'),
    /**
     * `cards.cover_colour` — see the module header. Bounded, not enumerated:
     * a value outside `BE-25.2`'s ten-member palette is stale data a reader
     * should tolerate, not a value inexpressible in the column.
     */
    colour: z.string().max(32),
    size: coverSizeSchema,
    textTreatment: coverTextTreatmentSchema,
  }),
  z.object({
    source: z.literal('photo_library'),
    /**
     * `cards.cover_photo_ref` — verbatim and opaque. Not a URL: no scheme is
     * assumed, nothing is fetched, and no format check runs beyond the
     * length bound the column itself imposes.
     */
    photoRef: z.string().max(2048),
    /**
     * `cards.cover_photo_attribution` — nullable, because not every photo
     * library entry carries one and `FR-CARD-025`'s attribution-preservation
     * edge case is satisfied by storing whatever the source gave, including
     * nothing.
     */
    photoAttribution: z.string().max(512).nullable(),
    size: coverSizeSchema,
    textTreatment: coverTextTreatmentSchema,
  }),
  z.object({
    source: z.literal('attachment'),
    /**
     * `cards.cover_attachment_id` — an id, never a URL. `BE-27.2`'s download
     * route resolves it later; nothing about this contract changes when it
     * does.
     */
    attachmentId: ulidSchema,
    size: coverSizeSchema,
    textTreatment: coverTextTreatmentSchema,
  }),
]);

export type CardCover = z.infer<typeof cardCoverSchema>;

// ---------------------------------------------------------------------------
// The BOARD-level cover setting — `boards.covers_enabled`, `FR-CARD-025` AC5.
// ---------------------------------------------------------------------------

/**
 * `boards.covers_enabled` on the wire — the read and the write half of
 * `FR-CARD-025` AC5: *"Given the board setting for covers is disabled, when
 * the board is viewed, then card fronts show no covers even for cards that
 * have one set."*
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE COLUMN HAS SHIPPED SINCE MIGRATION 0002 AND NO ROUTE HAS EVER TOUCHED
 * IT. THAT ABSENCE — NOT A MISSING COLUMN — IS THE WHOLE OF `FE-25.2`'S GAP
 * ---------------------------------------------------------------------------
 * `covers_enabled boolean NOT NULL DEFAULT true` sits in
 * `db/postgres/migrations/0002_boards_lists_cards.sql` beside `voting_enabled`,
 * `voting_hide_tallies` and `card_aging_mode`. Those three each acquired a
 * facet route; this one did not, so AC5 has been unbuildable rather than
 * unbuilt. **No migration is owed here and none was written** — this file adds
 * the two shapes `apps/server/src/routes/board-cover-settings.ts` needs and
 * nothing else.
 *
 * ---------------------------------------------------------------------------
 * ⚠ A BOARD SETTING IN `./card-cover`, NOT IN `./board`
 * ---------------------------------------------------------------------------
 * The noun decides the file, not the table: this is the switch that governs
 * whether the covers `cardCoverSchema` describes are *drawn*, so it belongs
 * with them the way `boardCardAgingModeSchema` belongs in `./card-aging`
 * rather than in `./board`. `./board`'s `boardSchema` serialises
 * `GET /boards/:id`, `POST /boards` and every board patch; putting the flag
 * there would ship it on every board rename, which is the same argument
 * `boardContentsSchema.labels` makes one file over.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE FLAG IS PROJECTED AND NOTHING IS FILTERED SERVER-SIDE
 * ---------------------------------------------------------------------------
 * AC5 describes what a card front *renders*, and the tempting reading is that
 * `GET /boards/:id/contents` should null every card's `cover` when this is
 * `false`. `apps/server/src/routes/board-contents.integration.test.ts` has
 * already ruled against that by name — *"covers_enabled=false does not
 * suppress the cover field or any other badge — project the fact, filter
 * nothing"* — which is B15's colourless-labels ruling one table over.
 * `boardContentsBoardSchema.coversEnabled` carries the fact; suppression is
 * the client's act, and re-enabling the setting must not require a second full
 * board read to recover covers the server had thrown away.
 */
export const boardCoverSettingsSchema = z.object({
  /**
   * The board this answer is about — `boardCardAgingModeSchema`'s exact
   * shape, and for its reason: a client may hold several facet responses in
   * flight and each has to identify itself once it has left the call that
   * requested it.
   */
  boardId: ulidSchema,
  /**
   * `boards.covers_enabled`. Required and non-nullable — the column is
   * `NOT NULL DEFAULT true`, so every board has an answer and there is no
   * third state to represent.
   */
  coversEnabled: z.boolean(),
});

export type BoardCoverSettings = z.infer<typeof boardCoverSettingsSchema>;

/**
 * The body of `PATCH /boards/:id/cover-settings`.
 *
 * ⚠ **`z.strictObject`, and one field** — SC-4's field-scoped patch, never an
 * object PUT. Inputs in this package are strict so a typo'd key is a 422
 * rather than a silently ignored write; views are `z.object`, which is why
 * `boardCoverSettingsSchema` above is not strict.
 *
 * ⚠ **The field is not optional.** `boardCardAgingModePatchSchema`'s call:
 * a one-field patch whose only field may be omitted is a request that asks
 * for nothing, and it would have to be refused at runtime by a `.refine`
 * that the type system cannot see. `PATCH` with `{}` is a 422 here, decided
 * by the schema.
 */
export const boardCoverSettingsPatchSchema = z.strictObject({
  coversEnabled: z.boolean(),
});

export type BoardCoverSettingsPatch = z.infer<typeof boardCoverSettingsPatchSchema>;
